// Framework-agnostic popover positioning, shared by the React
// InfoPopover component and any plain-HTML consumer (e.g. a server-
// rendered menu in a widget partial). Three layers:
//
//   1. computePlacement — PURE geometry. Given the boundary + trigger
//      rects and the popover's measured size, returns viewport coords.
//      No DOM access, so it's unit-testable in isolation.
//   2. positionPopover — the DOM bridge. Constrains the popover's width
//      to the boundary, measures the resulting (wrap-dependent) height,
//      runs computePlacement, and returns coords relative to a given
//      ancestor (so the popover can be position:absolute and scroll
//      WITH the page) or to the viewport when none is given.
//   3. watchPopover / createPopover — lifecycle. Reposition on
//      scroll/resize and dismiss on outside-click / Escape. createPopover
//      is the all-in-one vanilla controller; React wires watchPopover
//      into its own effect.
//
// Long-term this whole module is meant to be deletable in favor of CSS
// anchor positioning once Safari + Firefox ship it (see the
// project_v7_anchor_positioning note) — kept small on purpose.

export const VIEWPORT_MARGIN = 8
export const TRIGGER_GAP = 8
export const PREFERRED_WIDTH = 256

// Accepts a CSS selector, a DOM node, or a React-style ref ({ current }).
export const resolveBoundary = boundary => {
  if (!boundary) return null
  if (typeof boundary === 'string') return document.querySelector(boundary)
  if (boundary.current !== undefined) return boundary.current
  return boundary
}

// Pure: where the popover should sit, in viewport coords. Try below the
// trigger; flip above only when below doesn't fit but above does. Center
// horizontally on the trigger, then clamp into the boundary on both axes
// so it can never overflow (the vertical clamp is the last-resort fallback
// when neither above nor below fits cleanly).
export const computePlacement = ({
  boundaryRect, triggerRect, popoverWidth, popoverHeight,
}) => {
  const spaceBelow = boundaryRect.bottom - triggerRect.bottom - TRIGGER_GAP
  const spaceAbove = triggerRect.top - boundaryRect.top - TRIGGER_GAP
  const placeAbove = spaceBelow < popoverHeight && spaceAbove >= popoverHeight

  const top = placeAbove
    ? triggerRect.top - popoverHeight - TRIGGER_GAP
    : triggerRect.bottom + TRIGGER_GAP

  let left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2
  const minLeft = boundaryRect.left + VIEWPORT_MARGIN
  const maxLeft = boundaryRect.right - popoverWidth - VIEWPORT_MARGIN
  left = Math.max(minLeft, Math.min(left, maxLeft))

  const minTop = boundaryRect.top + VIEWPORT_MARGIN
  const maxTop = boundaryRect.bottom - popoverHeight - VIEWPORT_MARGIN
  const clampedTop = Math.max(minTop, Math.min(top, maxTop))

  return { top: clampedTop, left, placeAbove }
}

// DOM bridge. Mutates the popover's width/maxWidth (needed so the
// height we measure reflects the real text wrap), then returns
// { top, left }. With `relativeTo`, coords are relative to that element
// (use it as the popover's positioned ancestor → position:absolute, so
// it scrolls with the page); without it, coords are viewport-absolute.
// Does NOT set top/left — the caller applies them (React via state,
// vanilla via inline style) — so the same function serves both.
export const positionPopover = (triggerEl, popoverEl, { boundary, relativeTo } = {}) => {
  const boundaryEl = resolveBoundary(boundary) || document.documentElement
  const boundaryRect = boundaryEl.getBoundingClientRect()

  const maxWidth = Math.max(0, boundaryRect.width - VIEWPORT_MARGIN * 2)
  popoverEl.style.maxWidth = `${maxWidth}px`
  popoverEl.style.width = `${Math.min(PREFERRED_WIDTH, maxWidth)}px`

  const { top, left } = computePlacement({
    boundaryRect,
    triggerRect: triggerEl.getBoundingClientRect(),
    popoverWidth: popoverEl.offsetWidth,
    popoverHeight: popoverEl.offsetHeight,
  })

  const originEl = relativeTo ? resolveBoundary(relativeTo) : null
  const originRect = originEl ? originEl.getBoundingClientRect() : { top: 0, left: 0 }
  return { top: top - originRect.top, left: left - originRect.left }
}

// Lifecycle listeners shared by both consumers. `reposition` re-runs the
// placement (so the popover tracks its trigger through scroll/resize and
// re-flips near edges); `dismiss(reason)` fires on outside pointer-down
// ('outside') or Escape ('escape') so the caller can decide whether to
// return focus to the trigger. Capture-phase scroll catches ancestor
// scroll containers, not just the document. Returns a teardown.
export const watchPopover = (triggerEl, popoverEl, { reposition, dismiss }) => {
  const onPointerDown = e => {
    if (triggerEl.contains(e.target) || popoverEl.contains(e.target)) return
    dismiss('outside')
  }
  const onKeyDown = e => {
    if (e.key === 'Escape') dismiss('escape')
  }
  document.addEventListener('pointerdown', onPointerDown)
  document.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', reposition)
  window.addEventListener('scroll', reposition, true)
  return () => {
    document.removeEventListener('pointerdown', onPointerDown)
    document.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('resize', reposition)
    window.removeEventListener('scroll', reposition, true)
  }
}

// All-in-one vanilla controller for a trigger + popover element pair that
// already exist in the DOM. The popover must live inside a positioned
// ancestor (its parent by default, or pass `relativeTo`) so it can be
// position:absolute and scroll with the page rather than detaching like
// position:fixed would. The popover starts hidden ([hidden]); open()
// reveals it at visibility:hidden first so its size is measurable without
// a flash, positions it, then shows it. Returns { open, close, toggle,
// destroy }.
export const createPopover = (triggerEl, popoverEl, { boundary, relativeTo } = {}) => {
  const origin = relativeTo || popoverEl.parentElement
  let stopWatching = null

  const reposition = () => {
    const { top, left } = positionPopover(triggerEl, popoverEl, { boundary, relativeTo: origin })
    popoverEl.style.top = `${top}px`
    popoverEl.style.left = `${left}px`
  }

  const open = () => {
    if (stopWatching) return
    popoverEl.hidden = false
    popoverEl.style.visibility = 'hidden'
    triggerEl.setAttribute('aria-expanded', 'true')
    reposition()
    popoverEl.style.visibility = ''
    stopWatching = watchPopover(triggerEl, popoverEl, {
      reposition,
      dismiss: reason => {
        close()
        if (reason === 'escape') triggerEl.focus()
      },
    })
  }

  const close = () => {
    if (!stopWatching) return
    stopWatching()
    stopWatching = null
    popoverEl.hidden = true
    triggerEl.setAttribute('aria-expanded', 'false')
  }

  const toggle = () => (stopWatching ? close() : open())

  triggerEl.addEventListener('click', toggle)
  return {
    open,
    close,
    toggle,
    destroy () {
      close()
      triggerEl.removeEventListener('click', toggle)
    },
  }
}
